import React, { useEffect, useMemo, useState } from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import toast from 'react-hot-toast';
import Card from '../common/Card';
import Button from '../common/Button';
import Tabs from '../common/Tabs';
import Input from '../common/Input';
import api from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/formatters';
import '../../styles/ai.css';

const TABS = [
  { id: 'overview', label: 'Executive' },
  { id: 'sales', label: 'Sales AI' },
  { id: 'inventory', label: 'Inventory AI' },
  { id: 'customers', label: 'Customers' },
  { id: 'advisor', label: 'Advisor' },
  { id: 'assistant', label: 'Ask AI' },
  { id: 'alerts', label: 'Alerts' },
  { id: 'report', label: 'Daily Report' }
];

const ScoreRing = ({ score, label }) => (
  <div className="ai-score-ring">
    <div className="ai-score-value">{Math.round(score ?? 0)}</div>
    <div className="ai-score-label">{label}</div>
  </div>
);

const InsightCard = ({ title, body }) => (
  <div className="ai-insight-card">
    <h4>{title}</h4>
    <p>{body}</p>
  </div>
);

const AiDashboard = () => {
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [report, setReport] = useState(null);
  const [question, setQuestion] = useState('');
  const [askLoading, setAskLoading] = useState(false);
  const [answer, setAnswer] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  useEffect(() => {
    if (tab === 'report') loadReport();
    if (tab === 'alerts') loadAlerts();
    if (tab === 'advisor') loadRecommendations();
  }, [tab]);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const res = await api.get('/ai.php/dashboard');
      setDashboard(res.data.dashboard);
      setRecommendations(res.data.recommendations || []);
      setAlerts(res.data.alerts || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load AI dashboard');
    } finally {
      setLoading(false);
    }
  };

  const refreshCompute = async () => {
    setRefreshing(true);
    try {
      const res = await api.post('/ai.php/compute');
      setDashboard(res.data.dashboard);
      toast.success('AI analytics refreshed');
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Compute failed');
    } finally {
      setRefreshing(false);
    }
  };

  const loadReport = async () => {
    try {
      const res = await api.get('/ai.php/report');
      setReport(res.data.report);
    } catch (err) {
      console.error(err);
    }
  };

  const loadAlerts = async () => {
    try {
      const res = await api.get('/ai.php/alerts');
      setAlerts(res.data.alerts || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadRecommendations = async () => {
    try {
      const res = await api.get('/ai.php/recommendations');
      setRecommendations(res.data.recommendations || []);
    } catch (err) {
      console.error(err);
    }
  };

  const askAi = async (e) => {
    e?.preventDefault?.();
    if (!question.trim()) return;
    setAskLoading(true);
    try {
      const res = await api.post('/ai.php/ask', { question });
      setAnswer(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Assistant failed');
    } finally {
      setAskLoading(false);
    }
  };

  const markAlertsRead = async () => {
    try {
      await api.post('/ai.php/alerts/read', {});
      toast.success('Alerts marked read');
      loadAlerts();
    } catch (err) {
      toast.error('Failed to update alerts');
    }
  };

  const resolveRec = async (id) => {
    try {
      await api.post('/ai.php/recommendations/resolve', { recommendation_id: id });
      loadRecommendations();
    } catch (err) {
      toast.error('Failed to resolve');
    }
  };

  const health = dashboard?.health || {};
  const sales = dashboard?.sales || {};
  const inventory = dashboard?.inventory || {};
  const customers = dashboard?.customers || {};
  const forecasts = dashboard?.forecasts || {};

  const trendData = useMemo(
    () => (sales.daily_trend || []).map((d) => ({
      date: d.d || d.date,
      revenue: Number(d.revenue || 0),
      orders: Number(d.orders || 0)
    })),
    [sales]
  );

  if (loading) {
    return <div className="ai-loading">Loading AI Business Intelligence…</div>;
  }

  if (!dashboard) {
    return (
      <div className="ai-module">
        <div className="ai-header">
          <div>
            <h1>AI Business Intelligence</h1>
            <p>Could not load analytics for this business.</p>
          </div>
          <Button variant="primary" loading={refreshing} onClick={refreshCompute}>
            Retry / Refresh AI
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-module">
      <div className="ai-header">
        <div>
          <h1>AI Business Intelligence</h1>
          <p>
            Tenant-isolated insights ·             Last computed:{' '}
            {dashboard?.generated_at
              ? formatDate(dashboard.generated_at)
              : (dashboard?.computed_at ? formatDate(dashboard.computed_at) : '—')}
          </p>
        </div>
        <Button variant="primary" loading={refreshing} onClick={refreshCompute}>
          Refresh AI
        </Button>
      </div>

      <Tabs tabs={TABS} activeTab={tab} onTabChange={setTab} />

      {tab === 'overview' && (
        <div className="ai-section">
          <div className="ai-score-grid">
            <ScoreRing score={health.business_health_score} label="Business Health" />
            <ScoreRing score={health.inventory_health} label="Inventory Health" />
            <ScoreRing score={health.cash_flow_health} label="Cash Flow" />
            <ScoreRing score={health.forecast_accuracy} label="Forecast Confidence" />
            <ScoreRing score={100 - (health.risk_score || 0)} label="Risk Safety" />
          </div>

          <div className="ai-kpi-grid">
            <Card>
              <h4>Sales growth</h4>
              <strong>{health.sales_growth ?? 0}%</strong>
              <p className="ai-explain">{health.explanations?.revenue}</p>
            </Card>
            <Card>
              <h4>MTD revenue</h4>
              <strong>{formatCurrency(sales.revenue_this_month)}</strong>
              <p className="ai-explain">vs last month {formatCurrency(sales.revenue_last_month)}</p>
            </Card>
            <Card>
              <h4>Next month forecast</h4>
              <strong>{formatCurrency(forecasts.monthly_sales)}</strong>
              <p className="ai-explain">Confidence {forecasts.confidence}%</p>
            </Card>
            <Card>
              <h4>Customer growth (30d)</h4>
              <strong>{customers.new_customers_30d ?? 0}</strong>
              <p className="ai-explain">{health.explanations?.inventory}</p>
            </Card>
          </div>

          <Card title="Revenue trend with AI explanation">
            <p className="ai-explain">{sales.revenue_explanation}</p>
            <div className="ai-chart">
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                  <Legend />
                  <Line type="monotone" dataKey="revenue" stroke="#2C9BD9" strokeWidth={2} name="Revenue" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <div className="ai-two-col">
            <Card title="Priority recommendations">
              {(recommendations || []).slice(0, 5).map((r) => (
                <div key={r.id} className={`ai-rec ai-pri-${r.priority}`}>
                  <strong>{r.title}</strong>
                  <p>{r.reason}</p>
                  <small>Impact: {r.business_impact} · Confidence {r.confidence}%</small>
                </div>
              ))}
              {!recommendations?.length && <p>No open recommendations.</p>}
            </Card>
            <Card title="Open alerts">
              {(alerts || []).slice(0, 5).map((a) => (
                <div key={a.id} className={`ai-alert ai-sev-${a.severity}`}>
                  <strong>{a.title}</strong>
                  <p>{a.message}</p>
                </div>
              ))}
              {!alerts?.length && <p>No open alerts.</p>}
            </Card>
          </div>
        </div>
      )}

      {tab === 'sales' && (
        <div className="ai-section">
          <div className="ai-kpi-grid">
            <Card><h4>Avg order value</h4><strong>{formatCurrency(sales.average_order_value)}</strong></Card>
            <Card><h4>Avg basket size</h4><strong>{sales.average_basket_size}</strong></Card>
            <Card><h4>Growth</h4><strong>{sales.sales_growth_pct}%</strong></Card>
          </div>
          <InsightCard title="AI explanation" body={sales.revenue_explanation || '—'} />
          <div className="ai-two-col">
            <Card title="Top selling products">
              <ul className="ai-list">
                {(sales.top_selling_products || []).map((p) => (
                  <li key={p.id}>
                    <span>{p.product_name}</span>
                    <strong>{formatCurrency(p.revenue)}</strong>
                  </li>
                ))}
              </ul>
            </Card>
            <Card title="Declining products">
              <ul className="ai-list">
                {(sales.declining_products || []).map((p) => (
                  <li key={p.id}>
                    <span>{p.product_name}</span>
                    <strong>{p.recent_qty} vs {p.prior_qty}</strong>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
          <Card title="Sales by hour">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={sales.sales_by_hour || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="h" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="revenue" fill="#2C9BD9" name="Revenue" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
          <Card title="Best categories">
            <ul className="ai-list">
              {(sales.best_categories || []).map((c, i) => (
                <li key={i}><span>{c.name}</span><strong>{formatCurrency(c.revenue)}</strong></li>
              ))}
            </ul>
          </Card>
        </div>
      )}

      {tab === 'inventory' && (
        <div className="ai-section">
          <div className="ai-kpi-grid">
            <Card><h4>Inventory value</h4><strong>{formatCurrency(inventory.inventory_value)}</strong></Card>
            <Card><h4>Health score</h4><strong>{inventory.inventory_health_score}/100</strong></Card>
            <Card><h4>Reorder now</h4><strong>{(inventory.reorder_today || []).length}</strong></Card>
            <Card><h4>Dead stock SKUs</h4><strong>{(inventory.dead_stock || []).length}</strong></Card>
          </div>
          <div className="ai-two-col">
            <Card title="Products to reorder">
              <ul className="ai-list">
                {(inventory.reorder_today || []).slice(0, 12).map((p) => (
                  <li key={p.id}>
                    <span>{p.product_name} (~{p.days_remaining}d)</span>
                    <strong>Qty {p.recommended_order_qty}</strong>
                  </li>
                ))}
              </ul>
            </Card>
            <Card title="Dead / slow stock">
              <ul className="ai-list">
                {(inventory.dead_stock || []).slice(0, 12).map((p) => (
                  <li key={p.id}><span>{p.product_name}</span><strong>Stock {p.current_stock}</strong></li>
                ))}
              </ul>
            </Card>
          </div>
          <Card title="Stock-out forecasts">
            <ul className="ai-list">
              {(inventory.product_forecasts || []).slice(0, 15).map((p) => (
                <li key={p.id}>
                  <span>{p.product_name}</span>
                  <strong>{p.days_remaining} days · {p.stockout_date || '—'}</strong>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}

      {tab === 'customers' && (
        <div className="ai-section">
          <div className="ai-kpi-grid">
            <Card><h4>Total customers</h4><strong>{customers.total_customers}</strong></Card>
            <Card><h4>New (30d)</h4><strong>{customers.new_customers_30d}</strong></Card>
            <Card><h4>VIP</h4><strong>{(customers.vip_customers || []).length}</strong></Card>
            <Card><h4>Churn risk</h4><strong>{(customers.churn_risk || []).length}</strong></Card>
          </div>
          <Card title="Purchase predictions">
            {(customers.purchase_predictions || []).map((p, i) => (
              <InsightCard key={i} title={`${p.customer_name} → ${p.product_name}`} body={p.message} />
            ))}
            {!(customers.purchase_predictions || []).length && <p>Not enough repeat purchase history yet.</p>}
          </Card>
          <div className="ai-two-col">
            <Card title="VIP customers">
              <ul className="ai-list">
                {(customers.vip_customers || []).slice(0, 10).map((c) => (
                  <li key={c.id}>
                    <span>{c.customer_name || `#${c.id}`}</span>
                    <strong>{formatCurrency(c.spend)}</strong>
                  </li>
                ))}
              </ul>
            </Card>
            <Card title="Churn risk">
              <ul className="ai-list">
                {(customers.churn_risk || []).slice(0, 10).map((c) => (
                  <li key={c.id}>
                    <span>{c.customer_name || `#${c.id}`}</span>
                    <strong>{c.churn_probability}%</strong>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      )}

      {tab === 'advisor' && (
        <div className="ai-section">
          <Card title="AI Business Advisor">
            {(recommendations || []).map((r) => (
              <div key={r.id} className={`ai-rec-full ai-pri-${r.priority}`}>
                <div className="ai-rec-head">
                  <span className="ai-badge">{r.priority}</span>
                  <strong>{r.title}</strong>
                  <Button size="sm" variant="secondary" onClick={() => resolveRec(r.id)}>Done</Button>
                </div>
                <p><em>Reason:</em> {r.reason}</p>
                <p><em>Impact:</em> {r.business_impact}</p>
                <p><em>Expected benefit:</em> {r.expected_benefit}</p>
                <p><em>Confidence:</em> {r.confidence}%</p>
              </div>
            ))}
            {!(recommendations || []).length && <p>No open recommendations. Refresh AI to generate.</p>}
          </Card>
        </div>
      )}

      {tab === 'assistant' && (
        <div className="ai-section">
          <Card title="Natural language assistant">
            <p className="ai-explain">Ask about top products, restock, dead stock, forecasts, suppliers, risks, or sales drops.</p>
            <form onSubmit={askAi} className="ai-ask-form">
              <Input
                name="question"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder='e.g. "What should I restock today?"'
              />
              <Button type="submit" variant="primary" loading={askLoading}>Ask</Button>
            </form>
            {answer && (
              <div className="ai-answer">
                <h3>Answer</h3>
                <p>{answer.answer}</p>
                {answer.data?.chart?.length > 0 && (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={answer.data.chart.map((d) => ({
                      date: d.d || d.date,
                      revenue: Number(d.revenue || 0)
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="revenue" stroke="#132248" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
                {Array.isArray(answer.data?.table) && answer.data.table.length > 0 && (
                  <ul className="ai-list">
                    {answer.data.table.slice(0, 12).map((row, i) => (
                      <li key={i}>
                        <span>{row.product_name || row.title || row.customer_name || row.supplier_name || JSON.stringify(row).slice(0, 80)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </Card>
        </div>
      )}

      {tab === 'alerts' && (
        <div className="ai-section">
          <div style={{ marginBottom: '1rem' }}>
            <Button variant="secondary" onClick={markAlertsRead}>Mark all read</Button>
          </div>
          {(alerts || []).map((a) => (
            <div key={a.id} className={`ai-alert ai-sev-${a.severity}`}>
              <div className="ai-rec-head">
                <span className="ai-badge">{a.severity}</span>
                <strong>{a.title}</strong>
                <small>{formatDate(a.created_at)}</small>
              </div>
              <p>{a.message}</p>
            </div>
          ))}
          {!(alerts || []).length && <p>No alerts.</p>}
        </div>
      )}

      {tab === 'report' && (
        <div className="ai-section">
          <Card title={`Daily AI report · ${report?.report_date || '—'}`}>
            <p className="ai-explain">{report?.summary}</p>
            {report?.payload && (
              <>
                <h4>Expected revenue</h4>
                <p>{formatCurrency(report.payload.expected_revenue)} (profit {formatCurrency(report.payload.expected_profit)})</p>
                <h4>Top products</h4>
                <ul className="ai-list">
                  {(report.payload.top_products || []).map((p, i) => (
                    <li key={i}><span>{p.product_name}</span><strong>{formatCurrency(p.revenue)}</strong></li>
                  ))}
                </ul>
                <h4>Reorder</h4>
                <ul className="ai-list">
                  {(report.payload.reorder || []).map((p, i) => (
                    <li key={i}><span>{p.product_name}</span><strong>{p.recommended_order_qty}</strong></li>
                  ))}
                </ul>
              </>
            )}
            {!report && <p>No report yet — click Refresh AI.</p>}
          </Card>
        </div>
      )}
    </div>
  );
};

export default AiDashboard;
